import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { APPLE_SPRING, revealDelay } from '../../lib/motionPresets';

type Props = {
  children: ReactNode;
  className?: string;
  /** Stagger index — higher values enter slightly later. */
  index?: number;
  /**
   * `lift` — default section reveal (y + scale).
   * `fade` — opacity only; safe around layoutId sliding pills (category filters).
   */
  variant?: 'lift' | 'fade';
};

/**
 * Lift + scale on mount. Opacity stays at 1 so this doesn't fight the route cross-fade.
 */
export function RevealOnMount({
  children,
  className = '',
  index = 0,
  variant = 'lift',
}: Props) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  if (variant === 'fade') {
    return (
      <motion.div
        className={className}
        initial={{ opacity: 1, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, delay: revealDelay(index), ease: [0.22, 0.08, 0.2, 1] }}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 1, y: 18, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ ...APPLE_SPRING, delay: revealDelay(index) }}
    >
      {children}
    </motion.div>
  );
}
